<template>
    <div class="min-h-screen flex">
        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog as="div" class="fixed inset-0 flex z-40 lg:hidden" @close="sidebarOpen = false">
                <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
                </TransitionChild>
                <TransitionChild as="template" enter="transition ease-in-out duration-300 transform" enter-from="-translate-x-full" enter-to="translate-x-0" leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0" leave-to="-translate-x-full">
                    <div class="relative flex-1 flex flex-col max-w-xs w-full bg-gray-100 focus:outline-none dark:bg-gray-800">
                        <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100" leave-to="opacity-0">
                            <div class="absolute top-0 right-0 -mr-12 pt-2">
                                <button type="button" class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" @click="sidebarOpen = false">
                                    <span class="sr-only">Close sidebar</span>
                                    <MenuAlt3Icon class="h-6 w-6 text-white" aria-hidden="true" />
                                </button>
                            </div>
                        </TransitionChild>
                        <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                            <side-nav @navigating="sidebarOpen = false"/>
                        </div>
                        <div class="flex border-t border-gray-200 dark:border-gray-700">
                            <profile :auth="this.auth" />
                        </div>
                    </div>
                </TransitionChild>
                <div class="flex-shrink-0 w-14" aria-hidden="true">
                    <!-- Force sidebar to shrink to fit close icon -->
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Static sidebar for desktop -->
        <div class="hidden lg:flex lg:flex-shrink-0">
            <div class="flex flex-col w-72">
                <!-- Sidebar component, swap this element with another sidebar if you like -->
                <div class="flex-1 flex flex-col min-h-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div class="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
                        <side-nav />
                    </div>
                    <div class="flex border-t border-gray-200 dark:border-gray-700">
                        <profile :auth="this.auth" />
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
            <div class="lg:hidden">
                <div class="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-1.5">
                    <logo class="h-8 w-auto inline"/>
                    <div>
                        <button type="button" class="-mr-3 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900" @click="sidebarOpen = true">
                            <span class="sr-only">Open sidebar</span>
                            <MenuIcon class="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
            <div class="flex-1 relative z-0 flex flex-col overflow-hidden">
                <flash-messages />
                <slot />
            </div>
        </div>
    </div>

    <TransitionRoot :show="isSearchDialogOpen" as="template" @after-leave="query = ''" appear>
        <Dialog as="div" class="relative z-10" @close="closeSearchModal()">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-800 bg-opacity-50 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100" leave="ease-in duration-200" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95">
                    <DialogPanel class="mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                        <Combobox @update:modelValue="onSelect">
                            <div class="relative">
                                <SearchIcon class="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400" aria-hidden="true" />
                                <ComboboxInput class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm" placeholder="Search..." @change="search($event.target.value)" autocomplete="off"/>
                            </div>

                            <div v-if="query === ''" class="border-t border-gray-100 py-14 px-6 text-center text-sm sm:px-14">
                                <GlobeIcon class="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" />
                                <p class="mt-4 font-semibold text-gray-900">Search for customers and requests</p>
                                <p class="mt-2 text-gray-500">Quickly access customers and requests by running a global search.</p>
                            </div>

                            <ComboboxOptions v-if="query !== ''" static class="max-h-80 scroll-pt-11 scroll-pb-2 space-y-2 overflow-y-auto pb-2">
                                <li v-for="[category, items] in Object.entries(filteredResults)" :key="category">
                                    <h2 class="bg-gray-100 py-2.5 px-4 text-xs font-semibold text-gray-900">
                                        {{ category.toUpperCase() }}
                                    </h2>
                                    <ul class="mt-2 text-sm text-gray-800" v-if="items.length">
                                        <ComboboxOption v-for="item in items" :key="item.id" :value="item" as="template" v-slot="{ active }">
                                            <li :class="['cursor-default select-none px-4 py-2', active && 'bg-reethi-purple text-white']">
                                                <div v-if="category === 'customers'" class="relative flex items-center space-x-3">
                                                    <div class="flex-shrink-0">
                                                        <div v-if="!item.photo" class="h-10 w-10 rounded-full bg-red-100"/>
                                                        <img v-else class="object-cover h-10 w-10 rounded-full bg-gray-200" :src="item.photo" :alt="item.id" />
                                                    </div>
                                                    <div class="flex-1 min-w-0">
                                                        <a href="#" class="focus:outline-none">
                                                            <span class="absolute inset-0" aria-hidden="true"></span>
                                                            <p :class="[active ? 'text-white' : 'text-gray-900']" class="text-sm font-medium">{{ item.name }}</p>
                                                            <p class="truncate">
                                                                <span v-if="!item.channels.length" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">-</span>
                                                                <div v-for="channel in item.channels">
                                                                    <span :class="[active ? 'text-white' : 'text-gray-500']" class="text-sm mr-2">{{ channel.value }}</span>
                                                                    <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{{ channel.name }}</span>
                                                                </div>
                                                            </p>
                                                        </a>
                                                    </div>
                                                </div>
                                                <div v-if="category === 'orders'" class="relative flex flex-row items-center space-x-3">
                                                    <div class="flex flex-grow flex-col">
                                                        <h2 class="mb-2">{{ item.reference }}</h2>
                                                        <div class="flex items-center">
                                                            <span :class="[active ? 'text-white' : 'text-gray-400']">{{ item.customer.name }}</span>
                                                            <span :class="[active ? 'text-white' : 'text-gray-200']" class="mx-1">&bull;</span>
                                                            <span :class="[active ? 'text-white' : 'text-gray-400']">{{ item.channels.length }} channel{{ item.channels.length === 1 ? '' : 's' }}</span>
                                                            <span :class="[active ? 'text-white' : 'text-gray-200']" class="mx-1">&bull;</span>
                                                            <span :class="[active ? 'text-white' : 'text-gray-400']">{{ item.images.length }} image{{ item.images.length === 1 ? '' : 's' }}</span>
                                                        </div>
                                                    </div>
                                                    <div class="flex -space-x-1">
                                                        <div v-if="!item.images.length" class="inline-block h-8 w-8 rounded-full ring-2 ring-white bg-red-100"/>
                                                        <img v-for="image in item.images" :key="image.id" class="object-cover inline-block h-8 w-8 rounded-full ring-2 ring-white bg-gray-200" :src="image.uploaded_image_file_name" :alt="image.id" />
                                                    </div>
                                                </div>
                                            </li>
                                        </ComboboxOption>
                                    </ul>
                                    <div v-else-if="category === 'orders'">
                                        <ComboboxOption :value="true" as="template" v-slot="{ active }">
                                            <div :class="[active && 'bg-purple-100 text-white']"
                                                 class="text-sm font-light py-4 px-2 flex flex-col text-reethi-purple justify-center items-center">
                                                <PlusIcon class="w-6 mb-2"/>
                                                <span class="reethi-purple">Register a new Customer with the name ({{ query }}) and Create Request</span>
                                            </div>
                                        </ComboboxOption>
                                    </div>
                                    <div v-else class="text-sm font-light py-4 px-2"> No matching {{ category }}</div>
                                </li>
                            </ComboboxOptions>
                        </Combobox>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import axios from 'axios';
import Logo from '@/Shared/Logo'
import Dropdown from '@/Shared/Dropdown'
import { Link } from '@inertiajs/inertia-vue3'
import FlashMessages from '@/Shared/FlashMessages'
import Profile from '@/Shared/LayoutSegments/Profile'
import SideNav from '@/Shared/LayoutSegments/SideNav'

import {
    PlusIcon,
    MenuIcon,
    GlobeIcon,
    SearchIcon,
    MenuAlt3Icon,
} from '@heroicons/vue/solid'

import { ref } from 'vue'
import {
    Dialog,
    Combobox,
    DialogTitle,
    DialogPanel,
    ComboboxInput,
    DialogOverlay,
    TransitionRoot,
    ComboboxOption,
    ComboboxOptions,
    TransitionChild,
} from '@headlessui/vue'

export default {
    components: {
        Link,
        Logo,
        Dialog,
        Profile,
        SideNav,
        Dropdown,
        Combobox,
        DialogPanel,
        DialogTitle,
        MenuAlt3Icon,
        ComboboxInput,
        FlashMessages,
        DialogOverlay,
        ComboboxOption,
        TransitionRoot,
        ComboboxOptions,
        TransitionChild,

        PlusIcon,
        MenuIcon,
        GlobeIcon,
        SearchIcon,
    },
    props: {
        auth: Object,
    },
    data() {
        return {
            query: '',
            filteredResults: {},
        }
    },
    setup() {
        const superKey = ref(false)
        const sidebarOpen = ref(false)
        const isSearchDialogOpen = ref(false);

        return {
            superKey,
            sidebarOpen,
            isSearchDialogOpen,
        }
    },
    mounted () {
        document.addEventListener('keydown', this.pageKeyDown);
        document.addEventListener('keyup', this.pageKeyUp);

        this.$bus.$on('openSearch', () => {
            this.openSearchModal();
        })
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.pageKeyDown);
        document.removeEventListener('keyup', this.pageKeyUp);
    },
    methods: {
        search(term) {
            this.query = term;
            axios.get("/global-search", { params: {search: this.query} }).then((response) => this.filteredResults = response.data)
        },
        onSelect($event) {
            if ($event.reference) {
                this.query = '';
                this.closeSearchModal();
                this.$inertia.visit(this.$route('orders.show', {reference: $event.reference}));
            } else if ($event.id && !$event.reference) {
                this.closeSearchModal();
                this.$echo.leave(`new_request`)
                this.$inertia.post(this.$route('orders.create.with_existing_customer', {id: $event.id}));
                this.query = '';
            } else if ($event === true) {
                this.closeSearchModal();
                this.$echo.leave(`new_request`)
                this.$inertia.post(this.$route('orders.create.with_customer', this.query));
                this.query = '';
            }
        },
        openSearchModal() {
            this.isSearchDialogOpen = true
        },
        closeSearchModal() {
            this.isSearchDialogOpen = false
        },
        pageKeyDown(event) {
            if (event.key === 'Control' || event.key === 'Meta') {
                this.superKey = true;
            } else if (this.superKey && event.key === 'Enter') {
                event.preventDefault()
                this.openSearchModal();
            }
        },
        pageKeyUp(event) {
            if (event.key === 'Control' || event.key === 'Meta') {
                this.superKey = false;
            }
        },
    }
}
</script>
